<template>
  <div v-if="isOpen" class="fixed inset-0 bg-gray-900 bg-opacity-70 backdrop-blur-sm flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-3xl bg-gray-800 text-gray-300 rounded-lg p-5 sm:p-6 flex flex-col gap-4 relative shadow-lg border border-gray-700 animate-scaleIn">

      <!-- Header -->
      <div class="flex items-center justify-between border-b border-gray-700 pb-3">
        <div class="flex items-center gap-2">
          <i class="fas fa-user-edit text-indigo-300 text-2xl"></i>
          <h2 class="text-lg sm:text-xl font-semibold text-indigo-300">เปลี่ยนสกินตัวละคร</h2>
        </div>
        <button
          @click="closeModal"
          class="text-gray-300 hover:text-indigo-300 transition-colors duration-200"
          :disabled="isSubmitting"
          aria-label="ปิด"
        >
          <i class="fas fa-times text-base"></i>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-10">
        <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
      </div>

      <template v-else>
        <!-- Summary -->
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div class="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm flex items-center justify-between">
            <span class="text-indigo-200">สกินปัจจุบัน</span>
            <span class="font-semibold text-white truncate">{{ currentLabel }}</span>
          </div>
          <div class="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm flex items-center justify-between">
            <span class="text-indigo-200">Cash</span>
            <span class="font-semibold text-white">{{ formatCash(userCash) }}</span>
          </div>
          <div class="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm flex items-center justify-between sm:col-span-1 col-span-2">
            <span class="text-indigo-200">สถานะ</span>
            <span class="font-semibold" :class="selectedImage ? 'text-white' : 'text-gray-400'">
              {{ selectedImage ? 'กำลังเลือก…' : 'ยังไม่ได้เลือก' }}
            </span>
          </div>
        </div>

        <!-- Tabs -->
        <div class="flex items-center border-b border-gray-700 -mb-1">
          <button
            @click="switchTab('change')"
            class="tab-btn"
            :class="activeTab === 'change' ? 'tab-active' : ''"
          >
            <i class="fas fa-exchange-alt text-xs"></i>
            <span>เปลี่ยนสกิน</span>
          </button>
          <button
            @click="switchTab('purchase')"
            class="tab-btn"
            :class="activeTab === 'purchase' ? 'tab-active' : ''"
          >
            <i class="fas fa-lock-open text-xs"></i>
            <span>ปลดล็อคสกิน</span>
          </button>
        </div>

        <!-- Confirmation -->
        <div
          v-if="showConfirmation"
          class="rounded-md border border-gray-700 bg-gray-800 p-4 flex flex-col gap-4"
        >
          <p class="text-sm text-indigo-200 text-center">
            คุณแน่ใจหรือไม่ที่จะปลดล็อคสกินนี้โดยใช้
            <span class="font-semibold text-white">{{ selectedImagePrice }}</span> cash?
          </p>
          <div class="grid grid-cols-2 gap-2">
            <button
              @click="showConfirmation = false"
              class="btn-secondary"
              :disabled="isSubmitting"
              aria-label="ยกเลิก"
            >
              <i class="fas fa-times text-xs mr-2"></i>ยกเลิก
            </button>
            <button
              @click="confirmUnlockImage"
              class="btn-primary"
              :disabled="isSubmitting"
              aria-label="ยืนยัน"
            >
              <span v-if="!isSubmitting"><i class="fas fa-check text-xs mr-2"></i>ยืนยัน</span>
              <div v-else class="spinner-sm"></div>
            </button>
          </div>
        </div>

        <!-- Main -->
        <div v-else class="flex flex-col gap-4">
          <!-- Change -->
          <div v-if="activeTab === 'change'">
            <div class="flex items-center justify-between mb-2">
              <h4 class="text-sm text-indigo-200">สกินที่สามารถใช้ได้</h4>
              <span class="text-xs text-indigo-200">ทั้งหมด {{ availableImages.length }} แบบ</span>
            </div>

            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-80 overflow-y-auto" id="style-1">
              <div
                v-for="image in availableImages"
                :key="image"
                class="skin-card"
                @click="selectImage(image)"
              >
                <div class="relative">
                  <img
                    :src="image"
                    alt="skin"
                    class="skin-img w-full h-32 sm:h-36"
                    :class="cardBorderClass(image)"
                  />
                  <div
                    v-if="currentCharacterImage === image"
                    class="badge badge-success"
                    title="ใช้งานอยู่"
                  >
                    <i class="fas fa-check"></i>
                  </div>
                </div>
                <div class="skin-caption">
                  <span class="truncate">{{ skinLabel(image) }}</span>
                </div>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-2 mt-3">
              <button class="btn-secondary" @click="closeModal" :disabled="isSubmitting">
                <i class="fas fa-times text-xs mr-2"></i>ปิด
              </button>
              <button
                class="btn-primary"
                @click="submitChange"
                :disabled="isSubmitting || !selectedImage || selectedImage === currentCharacterImage"
                aria-label="เปลี่ยนสกิน"
              >
                <span v-if="!isSubmitting"><i class="fas fa-exchange-alt text-xs mr-2"></i>เปลี่ยนสกิน</span>
                <div v-else class="spinner-sm"></div>
              </button>
            </div>
          </div>

          <!-- Purchase -->
          <div v-else-if="activeTab === 'purchase'">
            <div class="flex items-center justify-between mb-2">
              <h4 class="text-sm text-indigo-200">สกินพรีเมียม</h4>
              <span class="text-xs text-indigo-200">ปลดล็อคแล้ว {{ unlockedCount }} / {{ premiumImages.length }}</span>
            </div>

            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-80 overflow-y-auto" id="style-1">
              <div
                v-for="image in premiumImages"
                :key="image.key"
                class="skin-card"
                @click="selectImageForPurchase(image)"
              >
                <div class="relative">
                  <img
                    :src="image.url"
                    alt="skin"
                    class="skin-img w-full h-32 sm:h-36"
                    :class="cardBorderClass(image.url)"
                  />
                  <div
                    v-if="!image.isUnlocked"
                    class="locked-overlay"
                    :class="{'opacity-60': !isSubmitting, 'opacity-50': isSubmitting}"
                  >
                    <i class="fas fa-lock text-white text-sm mb-1"></i>
                    <span class="text-xs text-white bg-gray-800 bg-opacity-70 px-2 py-1 rounded">
                      {{ image.price }} cash
                    </span>
                  </div>
                  <div v-else class="badge badge-success" title="ปลดล็อคแล้ว">
                    <i class="fas fa-check"></i>
                  </div>
                </div>
                <div class="skin-caption">
                  <span class="truncate">{{ skinLabel(image.url) }}</span>
                </div>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-2 mt-3">
              <button class="btn-secondary" @click="closeModal" :disabled="isSubmitting">
                <i class="fas fa-times text-xs mr-2"></i>ปิด
              </button>
              <button
                class="btn-primary"
                @click="submitPurchase"
                :disabled="isSubmitting || !selectedImage || isUnlockedImage || userCash < selectedImagePrice"
                aria-label="ปลดล็อคสกิน"
              >
                <span v-if="!isSubmitting">
                  <i class="fas fa-unlock text-xs mr-2"></i>ปลดล็อคสกิน ({{ selectedImagePrice || 0 }})
                </span>
                <div v-else class="spinner-sm"></div>
              </button>
            </div>
          </div>
        </div>

        <!-- Messages -->
        <div v-if="errorMessage" class="text-center text-sm text-red-300 mt-1">
          {{ errorMessage }}
        </div>
        <div v-if="successMessage" class="text-center text-sm text-green-300 mt-1">
          {{ successMessage }}
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';

export default {
  name: 'ChangeImageModal',
  props: { isOpen: { type: Boolean, default: false } },
  emits: ['close'],
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();

    const activeTab = ref('change');
    const selectedImage = ref('');
    const selectedImagePrice = ref(50);
    const currentCharacterImage = ref('');
    const userCash = ref(0);
    const unlockedImages = ref([]);
    const loading = ref(true);
    const errorMessage = ref('');
    const successMessage = ref('');
    const isSubmitting = ref(false);
    const showConfirmation = ref(false);

    const freeImages = [
      'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F1.png?alt=media&token=ab292e26-7fa9-4e41-9ca7-8516c730845f',
      'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F2.png?alt=media&token=c007f433-6ab4-4c02-acdf-e7b987246d4b',
      'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F3.png?alt=media&token=c8460a3c-8c11-4a4c-928b-ccfa09a385be',
      'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F4.png?alt=media&token=812dbf65-cbde-44c0-bbed-600aadc2d22a',
      'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F5.png?alt=media&token=bd310014-b622-4fdf-80dd-1ebdead07a41',
      'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F6.png?alt=media&token=ec9cc5de-50c1-4c7f-b15e-220d1f3e226f',
    ];

    const premiumImages = computed(() => [
      { key: '7',  url: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F8.png?alt=media&token=60505391-e754-4207-bb60-348c9a30c456', price: 50,  isUnlocked: unlockedImages.value.includes('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F8.png?alt=media&token=60505391-e754-4207-bb60-348c9a30c456') },
      { key: '8',  url: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F9.png?alt=media&token=e8711a59-d39a-4baa-843b-38d9a5e76541', price: 50,  isUnlocked: unlockedImages.value.includes('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F9.png?alt=media&token=e8711a59-d39a-4baa-843b-38d9a5e76541') },
      { key: '9',  url: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F7.png?alt=media&token=2fcd6f19-1a49-4f4c-a371-bb7408f7315f', price: 100, isUnlocked: unlockedImages.value.includes('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F7.png?alt=media&token=2fcd6f19-1a49-4f4c-a371-bb7408f7315f') },
      { key: '10', url: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F10.png?alt=media&token=7f923d77-098e-4aa9-8e53-7ada13db0fdc', price: 100, isUnlocked: unlockedImages.value.includes('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F10.png?alt=media&token=7f923d77-098e-4aa9-8e53-7ada13db0fdc') },
      { key: '11', url: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F11.png?alt=media&token=f2b3367e-0df1-4d3c-8889-c46c5bd2f784', price: 100, isUnlocked: unlockedImages.value.includes('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F11.png?alt=media&token=f2b3367e-0df1-4d3c-8889-c46c5bd2f784') },
      { key: '12', url: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F13.png?alt=media&token=d2b35e1c-30f4-43a5-b975-2f9e599ac500', price: 100, isUnlocked: unlockedImages.value.includes('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F13.png?alt=media&token=d2b35e1c-30f4-43a5-b975-2f9e599ac500') },
      { key: '13', url: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F14.png?alt=media&token=9afd4c2f-9383-4819-ac83-05296229bcd6', price: 100, isUnlocked: unlockedImages.value.includes('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F14.png?alt=media&token=9afd4c2f-9383-4819-ac83-05296229bcd6') },
      { key: '14', url: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F15.png?alt=media&token=7d10976b-55d6-4f41-9a30-510afe93d3b1', price: 100, isUnlocked: unlockedImages.value.includes('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F15.png?alt=media&token=7d10976b-55d6-4f41-9a30-510afe93d3b1') },
      { key: '15', url: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F31.png?alt=media&token=27d52f3c-2d1d-4d39-a701-8f7d908e2b3e', price: 100, isUnlocked: unlockedImages.value.includes('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F31.png?alt=media&token=27d52f3c-2d1d-4d39-a701-8f7d908e2b3e') },
      { key: '16', url: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F32.png?alt=media&token=9ace8847-2732-4d60-b61f-41adf217b7a2', price: 100, isUnlocked: unlockedImages.value.includes('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F32.png?alt=media&token=9ace8847-2732-4d60-b61f-41adf217b7a2') },
      { key: '17', url: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F33.png?alt=media&token=301e7ec5-e3f5-4dca-a404-d99d8c443bf9', price: 100, isUnlocked: unlockedImages.value.includes('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F33.png?alt=media&token=301e7ec5-e3f5-4dca-a404-d99d8c443bf9') },
      { key: '18', url: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F34.png?alt=media&token=904213b0-c852-4676-895e-44464f44b72e', price: 100, isUnlocked: unlockedImages.value.includes('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F34.png?alt=media&token=904213b0-c852-4676-895e-44464f44b72e') },
      { key: '19', url: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F35.png?alt=media&token=947d6312-fc63-4379-b0ef-ebc40783de9e', price: 100, isUnlocked: unlockedImages.value.includes('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2F35.png?alt=media&token=947d6312-fc63-4379-b0ef-ebc40783de9e') },
    ]);

    const availableImages = computed(() => [
      ...freeImages,
      ...premiumImages.value.filter(img => img.isUnlocked).map(img => img.url),
    ]);

    const isUnlockedImage = computed(() => {
      return premiumImages.value.find(img => img.url === selectedImage.value)?.isUnlocked || false;
    });

    const unlockedCount = computed(() => premiumImages.value.filter(p => p.isUnlocked).length);

    const currentLabel = computed(() => skinLabel(currentCharacterImage.value));

    function skinLabel(url) {
      if (!url) return '—';
      const match = url.match(/character2%2F(\d+)\.png/);
      return match ? `สกิน #${match[1]}` : 'สกินพิเศษ';
    }

    function formatCash(n) {
      const v = Number(n || 0);
      try { return v.toLocaleString('th-TH'); } catch { return String(v); }
    }

    async function fetchUserData() {
      if (!user.value) {
        errorMessage.value = 'กรุณาล็อกอินก่อน';
        loading.value = false;
        return;
      }
      playerStore.init(user.value.uid);
      try {
        const userData = playerStore.userData;
        const characterData = playerStore.characterData;
        const characterPrivateData = playerStore.characterPrivateData;
        if (userData) userCash.value = userData.cash || 0; else errorMessage.value = 'ไม่พบข้อมูลผู้ใช้';
        if (characterData) currentCharacterImage.value = characterData.imageUrl || ''; else errorMessage.value = 'ไม่พบข้อมูลตัวละคร';
        if (characterPrivateData) unlockedImages.value = characterPrivateData.unlockedImages || []; else errorMessage.value = 'ไม่พบข้อมูลส่วนตัวของตัวละคร';
      } catch (err) {
        console.error('Error fetching data:', err);
        errorMessage.value = 'ไม่สามารถโหลดข้อมูลได้';
      } finally {
        loading.value = false;
      }
    }

    function selectImage(image) {
      if (isSubmitting.value) return;
      selectedImage.value = image;
      selectedImagePrice.value = premiumImages.value.find(img => img.url === image)?.price || 0;
      successMessage.value = '';
      errorMessage.value = '';
    }

    function selectImageForPurchase(image) {
      if (isSubmitting.value || image.isUnlocked) return;
      selectedImage.value = image.url;
      selectedImagePrice.value = image.price;
      successMessage.value = '';
      errorMessage.value = '';
    }

    function switchTab(tab) {
      if (isSubmitting.value) return;
      activeTab.value = tab;
      selectedImage.value = '';
      selectedImagePrice.value = 0;
      showConfirmation.value = false;
      successMessage.value = '';
      errorMessage.value = '';
    }

    function closeModal() {
      if (isSubmitting.value) return;
      selectedImage.value = '';
      errorMessage.value = '';
      successMessage.value = '';
      showConfirmation.value = false;
      activeTab.value = 'change';
      emit('close');
    }

    async function submitChange() {
      if (!selectedImage.value) {
        errorMessage.value = 'กรุณาเลือกสกิน';
        return;
      }
      if (selectedImage.value === currentCharacterImage.value) {
        errorMessage.value = 'สกินนี้ถูกเลือกอยู่แล้ว';
        return;
      }
      isSubmitting.value = true;
      errorMessage.value = '';
      successMessage.value = '';
      try {
        const characterActions = projectFunctions.httpsCallable('gameActions');
        await characterActions({ action: 'characterActions', subAction: 'changeCharacterImage', imageUrl: selectedImage.value });
        currentCharacterImage.value = selectedImage.value;
        successMessage.value = 'เปลี่ยนสกินตัวละครสำเร็จ';
        selectedImage.value = '';
        setTimeout(() => closeModal(), 1200);
      } catch (err) {
        console.error('Error changing skin:', err);
        errorMessage.value = err.message || 'เกิดข้อผิดพลาดในการเปลี่ยนสกิน';
      } finally {
        isSubmitting.value = false;
      }
    }

    function submitPurchase() {
      if (!selectedImage.value) {
        errorMessage.value = 'กรุณาเลือกสกิน';
        return;
      }
      if (isUnlockedImage.value) {
        errorMessage.value = 'สกินนี้ถูกปลดล็อคแล้ว';
        return;
      }
      if (userCash.value < (selectedImagePrice.value || 0)) {
        errorMessage.value = `เงินไม่เพียงพอ (ต้องใช้ ${selectedImagePrice.value} cash)`;
        return;
      }
      showConfirmation.value = true;
    }

    async function confirmUnlockImage() {
      isSubmitting.value = true;
      errorMessage.value = '';
      successMessage.value = '';
      try {
        const characterActions = projectFunctions.httpsCallable('gameActions');
        const result = await characterActions({
          action: 'characterActions',
          subAction: 'unlockCharacterImage',
          imageUrl: selectedImage.value,
          price: selectedImagePrice.value,
        });
        userCash.value = Math.max(0, (userCash.value || 0) - (result.data.cashDeduction || selectedImagePrice.value || 0));
        unlockedImages.value = Array.from(new Set([...(unlockedImages.value || []), selectedImage.value]));
        successMessage.value = 'ปลดล็อคสกินสำเร็จ';
        showConfirmation.value = false;
        setTimeout(() => { switchTab('change'); }, 900);
      } catch (err) {
        console.error('Error unlocking skin:', err);
        errorMessage.value = err.message || 'เกิดข้อผิดพลาดในการปลดล็อคสกิน';
        showConfirmation.value = false;
      } finally {
        isSubmitting.value = false;
      }
    }

    function cardBorderClass(image) {
      return selectedImage.value === image
        ? 'border-2 border-indigo-600  bg-gray-800'
        : 'border-2 border-gray-700 hover:border-indigo-500 bg-gray-800';
    }

    watch(() => props.isOpen, (open) => {
      if (open) {
        loading.value = true;
        fetchUserData();
      }
    });
    onMounted(() => {
      if (props.isOpen) fetchUserData();
    });

    return {
      isOpen: props.isOpen,
      activeTab,
      selectedImage,
      selectedImagePrice,
      currentCharacterImage,
      userCash,
      unlockedImages,
      loading,
      errorMessage,
      successMessage,
      isSubmitting,
      showConfirmation,
      premiumImages,
      availableImages,
      isUnlockedImage,
      unlockedCount,
      currentLabel,

      selectImage,
      selectImageForPurchase,
      submitChange,
      submitPurchase,
      confirmUnlockImage,
      closeModal,
      switchTab,
      skinLabel,
      formatCash,
      cardBorderClass,
    };
  },
};
</script>

<style scoped>
/* Tabs */
.tab-btn { @apply px-3 sm:px-4 py-2 text-sm font-medium text-indigo-200 hover:text-white transition-colors duration-200 border-b-2 border-transparent flex items-center gap-2; }
.tab-active { @apply text-white border-indigo-500; }

/* Buttons */
.btn-primary { @apply w-full bg-indigo-800 hover:bg-indigo-900 text-white py-2 rounded-md text-sm border border-indigo-900 transition-colors duration-200 flex justify-center items-center; }
.btn-secondary { @apply w-full bg-gray-800 hover:bg-gray-700 text-white py-2 rounded-md text-sm border border-gray-700 transition-colors duration-200 flex justify-center items-center; }

/* Summary chip */
.summary-chip { @apply bg-gray-900 border border-gray-800 rounded-md px-3 py-2 text-sm flex items-center justify-between; }

/* Spinner (small) */
.spinner-sm {
  width: 1rem; height: 1rem;
  border-top-width: 2px; border-right-width: 2px;
  border-color: rgba(255,255,255,0.9);
  border-style: solid;
  border-bottom-color: transparent; border-left-color: transparent;
  border-radius: 9999px; animation: spin 1s linear infinite; margin: 0 auto;
}

/* Card & image */
.skin-card { @apply rounded-md p-2 border border-gray-800 bg-gray-900 hover:bg-gray-800 transition-all duration-200 cursor-pointer; }
/* (นำ aspect-square ออกให้รองรับ Tailwind v2) */
.skin-img { @apply object-cover rounded-md; }
.skin-caption { @apply mt-2 text-xs text-indigo-200 text-center; }

/* Badges & overlay */
.badge { @apply absolute top-1 right-1 text-white text-xs rounded-full px-2 py-1; }
.badge-success { @apply bg-green-600; }
.locked-overlay { @apply absolute inset-0 flex flex-col items-center justify-center rounded-md bg-gray-900 bg-opacity-60; }

/* Animations */
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.25s ease-out; }
@keyframes scaleIn { from { transform: scale(0.98); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.25s ease-out; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Scrollbar */
#style-1 { scrollbar-width: thin; scrollbar-color: #4b5563 #1f2937; }
#style-1::-webkit-scrollbar { width: 6px; }
#style-1::-webkit-scrollbar-track { background: #1f2937; border-radius: 8px; }
#style-1::-webkit-scrollbar-thumb { background: #374151; border-radius: 8px; }
#style-1::-webkit-scrollbar-thumb:hover { background: #4b5563; }

/* Helper for Tailwind v2 (ถ้าธีมของคุณไม่มี max-h-80) */
.max-h-80 { max-height: 20rem; }
</style>